import Cookies from 'cookies-ts';

declare var jQuery:any;
declare var FullCalendar:any;
declare var adminSettings:any;
declare var product_page_localize:any;
const MOBILE_MEDIA_QUERY = '(max-width: 575.98px)';

export class MainClass {
    public calendar:any;
    public calendarEl:any;
    public calendarInitialized: boolean = false;
    private searchDebounceTimer: number | null = null;
    private searchOutsideHandler: ((e: Event) => void) | null = null;
    private mobileMediaQuery: MediaQueryList | null = null;
    private mobileMediaHandler: ((e: MediaQueryListEvent) => void) | null = null;
    private currentFilter: 'all' | 'my' = 'all';
    private currentSearch: string = '';

    constructor() {
        this.calendarEl = document.getElementById('calendar');
    }

    private i18n(key: string, fallback: string): string {
        const strings = (typeof adminSettings !== 'undefined' && adminSettings && adminSettings.i18n) || {};
        return (strings[key] as string) || fallback;
    }

    private isLoggedIn(): boolean {
        return typeof adminSettings !== 'undefined' && !!adminSettings.is_logged_in;
    }

    private isMobileViewport(): boolean {
      if (typeof window === 'undefined' || !window.matchMedia) return false;
      return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    }

    private buildHeaderToolbarConfig(isMobile: boolean) {
      if (isMobile) {
        return {
          left: 'prev,next',
          center: 'title',
          right: 'today listWeek,timeGridWeek,dayGridMonth',
        };
      }
      return {
        left: 'prevYear,prev,next,nextYear today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
      };
    }

    public init() {
      this.initCalendar();
      this.calendar.render();
    }

    public getValidLanguage(lang?: string): string | undefined {
      return lang && ['en_US', 'fr_FR'].includes(lang) ? lang : undefined;
    }

    public initCalendar() {
      const cookie = new Cookies();
      const cookieValue: string | undefined = cookie.get('wp_lang');
      const defaultLanguage = cookieValue ? cookieValue.split('_')[0] : 'en';
      
      this.calendar = new FullCalendar.Calendar(this.calendarEl, {
        locale: defaultLanguage,
        buttonText: adminSettings.buttonText || {},
        showNonCurrentDates: false,
        navLinks: true,
        editable: false,
        dayMaxEvents: true,
        displayEventTime: false,
        events: {
            url: adminSettings.admin_url,
            extraParams: () => ({
                action: 'get_events',
                nonce: adminSettings.nonce,
                filter: this.currentFilter,
                search: this.currentSearch,
            }),
        },
        eventDidMount: this.eventDidMount.bind(this),
        eventClick: this.eventClick.bind(this),
        viewDidMount: (viewInfo: any) => {
          this.onViewDidMount(viewInfo);
          this.calendarInitialized = true;
        },
        datesSet: this.onDatesSet.bind(this),
        loading: this.toggleLoading.bind(this),
        moreLinkClick: (info: any) => {
          // On Day view, "more" would navigate to same view; show popover instead
          if (this.calendar?.view?.type === 'timeGridDay') {
            return 'popover';
          }
          return 'day';
        },
      });

       // Render the calendar with the initial locale and headerToolbar
      this.calendar.render();

      // Update the locale and headerToolbar whenever needed
      this.updateCalendarOptions(defaultLanguage);

      // Track viewport so the toolbar config swaps automatically on resize.
      this.bindMobileMediaQuery();
    }

    private updateCalendarOptions(locale: string) {
      // Set the new locale
      this.calendar.setOption('locale', locale);

      // Set the headerToolbar based on the current viewport.
      this.applyHeaderToolbarForViewport();

      // Refetch events (if needed)
      this.calendar.refetchEvents();

      // Mount the toolbar search after FullCalendar has rendered the toolbar.
      this.mountToolbarSearch();
      this.mountToolbarFilter();
    }

    private applyHeaderToolbarForViewport() {
      if (!this.calendar) return;
      const config = this.buildHeaderToolbarConfig(this.isMobileViewport());
      this.calendar.setOption('headerToolbar', config);
      this.mountToolbarSearch();
      this.mountToolbarFilter();
    }

    private bindMobileMediaQuery() {
      if (typeof window === 'undefined' || !window.matchMedia) return;
      if (this.mobileMediaQuery && this.mobileMediaHandler) return;

      this.mobileMediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
      this.mobileMediaHandler = (_e: MediaQueryListEvent) => {
        this.applyHeaderToolbarForViewport();
      };

      if (typeof this.mobileMediaQuery.addEventListener === 'function') {
        this.mobileMediaQuery.addEventListener('change', this.mobileMediaHandler);
      } else if (typeof (this.mobileMediaQuery as any).addListener === 'function') {
        // Safari < 14 fallback
        (this.mobileMediaQuery as any).addListener(this.mobileMediaHandler);
      }
    }

    private mountToolbarSearch() {
      if (!this.calendarEl) return;

      const toolbar = this.calendarEl.querySelector('.fc-header-toolbar') as HTMLElement | null;
      if (!toolbar) return;

      // Idempotent — if already mounted inside the current toolbar, bail out.
      if (toolbar.querySelector('.vm-cal-search')) return;

      const chunks = toolbar.querySelectorAll('.fc-toolbar-chunk');
      const host: HTMLElement = (chunks.length > 0
        ? chunks[chunks.length - 1]
        : toolbar) as HTMLElement;

      const wrapper = document.createElement('div');
      wrapper.className = 'vm-cal-search';

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'vm-cal-search__toggle';
      toggle.setAttribute('aria-label', this.i18n('search_toggle_aria', 'Toggle event search'));
      toggle.setAttribute('aria-expanded', 'false');
      toggle.innerHTML =
        '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
        '<path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.7.7l.27.28v.79l5 5 1.49-1.49-5-5zm-6 0a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z"/>' +
        '</svg>';

      const panel = document.createElement('div');
      panel.className = 'vm-cal-search__panel';

      const input = document.createElement('input');
      input.type = 'search';
      input.className = 'vm-cal-search__input';
      input.placeholder = this.i18n('search_placeholder', 'Search events');
      input.setAttribute('aria-label', this.i18n('search_aria_label', 'Search events'));

      panel.appendChild(input);
      wrapper.appendChild(toggle);
      wrapper.appendChild(panel);
      host.prepend(wrapper);

      input.addEventListener('input', (e: Event) => {
        const value = (e.target as HTMLInputElement).value || '';
        this.scheduleSearch(value);
      });

      toggle.addEventListener('click', (e: Event) => {
        e.stopPropagation();
        const isOpen = wrapper.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        if (isOpen) {
          input.focus();
        }
      });

      // Prevent clicks inside the panel from triggering the outside-click close.
      panel.addEventListener('click', (e: Event) => {
        e.stopPropagation();
      });

      input.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          wrapper.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
          toggle.focus();
        }
      });

      // Outside-click closes the dropdown (only one listener at a time).
      if (this.searchOutsideHandler) {
        document.removeEventListener('click', this.searchOutsideHandler);
      }
      this.searchOutsideHandler = (evt: Event) => {
        if (!wrapper.classList.contains('is-open')) return;
        const target = evt.target as Node;
        if (!wrapper.contains(target)) {
          wrapper.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
        }
      };
      document.addEventListener('click', this.searchOutsideHandler);
    }

    private scheduleSearch(value: string) {
      if (this.searchDebounceTimer !== null) {
        window.clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = window.setTimeout(() => {
        this.searchDebounceTimer = null;
        this.runSearch(value);
      }, 400);
    }

    private runSearch(value: string) {
      if (!this.calendar) return;
      this.currentSearch = value;
      this.calendar.refetchEvents();
    }
    
    public render() {
        this.calendar.render();
    }

    public eventClick(eventClickInfo: any) {
      // Prevent the default behavior of the click event
      eventClickInfo.jsEvent.preventDefault();
  
      // Call the openModal function with the eventClickInfo parameter
      this.openModal(eventClickInfo);
  
      // Define a function to handle clicks outside the modal
      const closeModalOnClickOutside = (e: Event) => {
          // Get the modal ID
          const modal = document.getElementById('calendar-pop-over');
  
          // Check if the click event occurred inside the modal and not on the event element
          if (modal && modal.contains(e.target as Node) && e.target !== eventClickInfo.el) {
              // Call the hidePopOver function to close the modal
              this.hidePopOver(eventClickInfo);
  
              // Remove the click event listener to prevent further checks
              document.removeEventListener('click', closeModalOnClickOutside);
          }
      };
  
      // Add the click event listener to the document to handle clicks outside the modal
      document.addEventListener('click', closeModalOnClickOutside);
    }
  
    public openModal(eventClickInfo: any) {
      const parentRect = this.calendarEl.parentElement.getBoundingClientRect();
      const elemRect = eventClickInfo.el.getBoundingClientRect();
      const topoffset = elemRect.top - parentRect.top;
      const leftoffset = elemRect.left - parentRect.left;
      const calendarPopOver = document.getElementById('calendar-pop-over');

      jQuery(calendarPopOver.querySelector('.popover-title')).text(eventClickInfo.event.title);
      jQuery(calendarPopOver.querySelector('.popover-content')).html(eventClickInfo.event.extendedProps.description);

      let initTop = topoffset + jQuery(eventClickInfo.el).height() + 25;
      let initleft = leftoffset + 10 + (jQuery(eventClickInfo.el).width() - jQuery(calendarPopOver).width()) / 2;

      if (eventClickInfo.jsEvent.y / window.innerHeight > 0.60) {
        initTop -= jQuery(calendarPopOver).height() + 50;
        jQuery(calendarPopOver).removeClass('bottom').addClass('top');
      } else {
        jQuery(calendarPopOver).addClass('bottom').removeClass('top');
      }

      jQuery(calendarPopOver).css('top', initTop + 'px');
      jQuery(calendarPopOver).css('left', initleft + 'px');
      jQuery(calendarPopOver).show();
    }

    public destroy() {
        this.calendar.destroy();
    }

    public currentDateStart() {
        let startUTCDate =  this.calendar.view.currentStart.toUTCString();
        let startDate = new Date(startUTCDate);

        return startDate.toISOString();
    }

    public currentDateEnd() {
        let endUTCDate =  this.calendar.view.currentEnd.toUTCString();
        let endDate = new Date(endUTCDate);

        return endDate.toISOString();
    }

    public hidePopOver(eventClickInfo:any) {
        jQuery('#calendar-pop-over').hide();
    }

    public get_data(searchEvent: any, start: any, end: any) {
      // Ensure that the calendar is properly initialized
      if (!this.calendarInitialized) {
          this.initCalendar();
      }
  
      // Set the initial date before loading events
      this.calendar.setOption('initialDate', start);
  
      // Load events from the server
      this.calendar.setOption('events', {
          url: product_page_localize.ajax_url,
          extraParams: {
              action: 'get_events',
              search: searchEvent,
              start: start,
              end: end,
              nonce: adminSettings.nonce,
          },
      });
  
      // Render the calendar
      this.calendar.render();
    }

    public getOffset(el:any) {
        const rect = el.getBoundingClientRect();
        return {
            left: rect.left + window.scrollX,
            top: rect.top + window.scrollY
        };
    }

    private mountToolbarFilter() {
      if (!this.calendarEl) return;
      if (!this.isLoggedIn()) return;

      const toolbar = this.calendarEl.querySelector('.fc-header-toolbar') as HTMLElement | null;
      if (!toolbar) return;

      if (toolbar.querySelector('.vm-cal-filter')) return;

      const chunks = toolbar.querySelectorAll('.fc-toolbar-chunk');
      const host: HTMLElement = (chunks.length > 0 ? chunks[0] : toolbar) as HTMLElement;

      const group = document.createElement('div');
      group.className = 'vm-cal-filter';
      group.setAttribute('role', 'radiogroup');
      group.setAttribute('aria-label', this.i18n('filter_aria_label', 'Filter events'));

      const options: Array<{ value: 'all' | 'my'; label: string }> = [
        { value: 'all', label: this.i18n('all_events', 'All Events') },
        { value: 'my',  label: this.i18n('my_events',  'My Events') },
      ];

      const buttons: HTMLButtonElement[] = [];
      options.forEach((opt) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'vm-cal-filter__option';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('data-value', opt.value);
        btn.textContent = opt.label;
        const isChecked = this.currentFilter === opt.value;
        btn.setAttribute('aria-checked', isChecked ? 'true' : 'false');
        btn.setAttribute('tabindex', isChecked ? '0' : '-1');
        if (isChecked) btn.classList.add('is-active');

        btn.addEventListener('click', () => this.selectFilter(opt.value, buttons));
        btn.addEventListener('keydown', (e: KeyboardEvent) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            const idx = buttons.indexOf(btn);
            const next = buttons[(idx + 1) % buttons.length];
            next.focus();
            this.selectFilter(next.getAttribute('data-value') as 'all' | 'my', buttons);
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            const idx = buttons.indexOf(btn);
            const prev = buttons[(idx - 1 + buttons.length) % buttons.length];
            prev.focus();
            this.selectFilter(prev.getAttribute('data-value') as 'all' | 'my', buttons);
          }
        });

        buttons.push(btn);
        group.appendChild(btn);
      });

      host.appendChild(group);
    }

    private selectFilter(value: 'all' | 'my', buttons: HTMLButtonElement[]) {
      if (!this.calendar) return;
      if (this.currentFilter === value) return;
      this.currentFilter = value;

      buttons.forEach((b) => {
        const isActive = b.getAttribute('data-value') === value;
        b.setAttribute('aria-checked', isActive ? 'true' : 'false');
        b.setAttribute('tabindex', isActive ? '0' : '-1');
        b.classList.toggle('is-active', isActive);
      });

      this.calendar.refetchEvents();
    }

    public onViewDidMount(viewInfo: any) {
      this.mountToolbarSearch();
      this.mountToolbarFilter();
      if (viewInfo.view.type === 'dayGridMonth') {
        const checkInterval = setInterval(() => {
          const elements: any = document.getElementsByClassName('fc-daygrid-event-harness');
          if (elements.length > 0) {
            this.hideDivsWithHiddenVisibility(elements);
            clearInterval(checkInterval);
          }
        }, 2000);
      }
    }
  
    public hideDivsWithHiddenVisibility(elements: any) {
      for (let i = 0; i < elements.length; i++) {
        const element = elements[i] as HTMLElement;
        const visibility = jQuery(element).css('visibility');
        if (visibility === 'hidden' || visibility === 'none') {
          element.style.display = 'none';
        }
      }
    }
  
    public onDatesSet(dateInfo: any) {
      // Close the Details popup when switching views or navigating dates
      this.hidePopOver(null);
      this.onViewDidMount(dateInfo);
    }

    private eventDidMount(info: any) {
      const element = info.el;
      const event = info.event;
      const productId = event.extendedProps.productId;
      element.style.visibility = 'visible';

      /*jQuery.post(
        adminSettings.admin_url,
        { action: 'set_timeline_color', productID: productId, nonce: adminSettings.nonce }
      )
        .done((response: any) => {
          element.style.visibility = 'visible';
          if (element.classList.contains('fc-daygrid-block-event')) {
            //element.style.background = response.color;
          }
        })
        .fail((jqXHR: any, textStatus: any, errorThrown: any) => {
          console.error('Error setting timeline color:', textStatus, errorThrown);
        });
      */
    }
  
    private toggleLoading(bool: boolean) {
      const loadingElement = document.getElementById('loading');
      if (loadingElement) {
        loadingElement.style.display = bool ? 'block' : 'none';
      }
    }
}
